<script setup lang="ts">
import { ref, defineProps, computed, watch, onMounted } from 'vue'

// Define Tab Interface with `visible` Property
interface Tab {
  name: string;
  label: string;
  visible?: boolean; // ✅ Optional: Controls tab visibility
}

// Define Props
const props = defineProps<{
  tabs: Tab[];
  modelValue: string; // ✅ Parent can control active tab via v-model
  textSize?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

// Filter visible tabs
const visibleTabs = computed(() => props.tabs.filter(tab => tab.visible !== false));

// Reactive state for active tab
const activeTab = ref<string>(props.modelValue || (visibleTabs.value.length > 0 ? visibleTabs.value[0].name : ''));

// Watch for external changes to modelValue
watch(() => props.modelValue, (newTab) => {
  if (visibleTabs.value.some(tab => tab.name === newTab)) {
    activeTab.value = newTab;
  }
});

// Function to change active tab
const setActiveTab = (tabName: string) => {
  activeTab.value = tabName;
  emit('update:modelValue', tabName); // ✅ Emit to parent
};


onMounted(async () => {
  let tabs = props.tabs.filter((e) => e.visible == true)
  if (tabs.length == 1) {
    setActiveTab(tabs[0].name)
  }
})
</script>

<template>
  <div class="mb-6 border-b border-gray-200 dark:border-gray-700">
    <div class="flex justify-between items-center mb-4">
      <!-- Tabs (Only Show Visible Tabs) -->
      <div>
        <button
        v-for="tab in visibleTabs"
        :key="tab.name"
        @click="setActiveTab(tab.name)"
        class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
        :class="[ 
          activeTab === tab.name
            ? 'border-blue-600 text-blue-600 dark:text-blue-400'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
        ]"
        :aria-selected="activeTab === tab.name ? 'true' : 'false'"
        :aria-controls="tab.name"
        role="tab"
      >
      <h3 :class="[!props.textSize ? props.textSize : 'text-lg']" class="font-medium text-gray-900 dark:text-white">{{tab.label}}</h3>
      </button>
      </div>
      <slot name="buttons"></slot>
    </div>    
  </div>

  <!-- Tab Content (Only Show Visible Tabs) -->
  <div v-for="tab in visibleTabs" :key="tab.name" v-show="activeTab === tab.name" role="tabpanel" :aria-labelledby="tab.name">
    <slot :name="tab.name"></slot>
  </div>
</template>

<style scoped>
/* Optional: Custom tab styles */
button {
  cursor: pointer;
}

button[aria-selected='true'] {
  font-weight: bold;
}

.tab-content {
  margin-top: 20px;
}
</style>
